import React, { useEffect, useRef, useState } from 'react';
import SortableListItem from '@/components/ui/drag-and-drop/sortable-list-item/SortableListItem';
import { Icon, IconButton, TextField } from '@monash/portal-react';
import IconInput from '@/components/pages/custom/components/icon-input/IconInput';
import { deepClone } from '@monash/portal-frontend-common';
import { getShortcut, getShortcutIndex } from '../../utils';
import { arrayMove } from '@dnd-kit/sortable';
import c from './edit-shortcut-desktop-row.module.scss';

export const EditShortcutDesktopRow = ({
  index,
  shortcut,
  currentShortcut,
  isShortcutActive,
  setInEditShortcuts,
  inEditShortcuts,
  validateShortcuts,
}) => {
  const [isNameValid, setIsNameValid] = useState(
    currentShortcut ? shortcut.name : null
  );
  const [isUrlValid, setIsUrlValid] = useState(
    currentShortcut ? shortcut.url : null
  );
  const [shouldScrollIntoView, setShouldScrollIntoView] = useState(false);
  const itemRef = useRef(null);

  const isFirst = index === 0;
  const isLast = index === inEditShortcuts.length - 1;

  const deleteInEditShortcut = () => {
    setInEditShortcuts((f) => {
      const newShortcuts = deepClone(f);
      const inEditShortcutIndex = getShortcutIndex(shortcut.id, newShortcuts);
      newShortcuts.splice(inEditShortcutIndex, 1);
      validateShortcuts(newShortcuts);
      return newShortcuts;
    });
  };

  const moveShortcutUp = () => {
    setInEditShortcuts((prevShortcuts) => {
      const newShortcuts = deepClone(prevShortcuts);
      const currentIndex = getShortcutIndex(shortcut.id, newShortcuts);

      // Can't move up if already at the top
      if (currentIndex === 0) return prevShortcuts;

      setShouldScrollIntoView(true);
      return arrayMove(newShortcuts, currentIndex, currentIndex - 1);
    });
  };

  const moveShortcutDown = () => {
    setInEditShortcuts((prevShortcuts) => {
      const newShortcuts = deepClone(prevShortcuts);
      const currentIndex = getShortcutIndex(shortcut.id, newShortcuts);

      // Can't move down if already at the bottom
      if (currentIndex === newShortcuts.length - 1) return prevShortcuts;

      setShouldScrollIntoView(true);
      return arrayMove(newShortcuts, currentIndex, currentIndex + 1);
    });
  };

  const updateInEditShortcuts = (value, key, validateUpdates = false) => {
    setInEditShortcuts((f) => {
      const newShortcuts = deepClone(f);
      const inEditShortcut = getShortcut(shortcut.id, newShortcuts);
      inEditShortcut[key] = value;
      if (validateUpdates) {
        validateShortcuts(newShortcuts);
      }
      return newShortcuts;
    });
  };

  // Validate shortcut when shortcut is active (in drag and drop action), so the validation statuses of both drag overlay and draggable items are consistent
  useEffect(() => {
    if (isShortcutActive) {
      setIsNameValid(shortcut.name);
      setIsUrlValid(shortcut.url);
    }
  }, [isShortcutActive]);

  // Scroll into view after reordering. Using useEffect here will
  // wait for react to finish reordering before applying the scroll
  useEffect(() => {
    if (shouldScrollIntoView && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setShouldScrollIntoView(false);
    }
  }, [shouldScrollIntoView, index]);

  return (
    <SortableListItem
      ref={itemRef}
      id={shortcut.id}
      className={c.inEditShortcut}
      itemLabel={shortcut.name}
    >
      <div className={c.fields}>
        <div className={c.topRow}>
          <div>
            <label className={c.label}>Icon</label>
            <IconInput
              className={c.iconInput}
              iconInput={shortcut.icon}
              setIconInput={(icon) => updateInEditShortcuts(icon, 'icon')}
              currentUrl={currentShortcut?.url}
              isShortcutActive={isShortcutActive}
              urlInput={shortcut.url}
              aria-haspopup="dialog"
            />
          </div>

          <div className={c.nameWrapper}>
            <label className={c.label}>Name (required)</label>
            <TextField
              value={shortcut.name}
              type="text"
              placeholder="Shortcut"
              error={isNameValid !== null ? () => !isNameValid : null}
              onChange={(e) => updateInEditShortcuts(e.target.value, 'name')}
              onBlur={() => {
                setIsNameValid(shortcut.name);
                validateShortcuts(inEditShortcuts);
              }}
              errorMsg="Name is required"
            />
          </div>
        </div>

        <div>
          <label className={c.label}>URL (required)</label>

          <TextField
            value={shortcut.url}
            type="url"
            placeholder="https://www.example.com/"
            error={isUrlValid !== null ? () => !isUrlValid : null}
            onChange={(e) => updateInEditShortcuts(e.target.value, 'url')}
            onBlur={() => {
              setIsUrlValid(shortcut.url);
              validateShortcuts(inEditShortcuts);
            }}
            onPaste={(e) => {
              e.preventDefault();
              const value = e.clipboardData.getData('Text');
              setIsUrlValid(value);
              updateInEditShortcuts(value, 'url', true);
            }}
            errorMsg="URL is required"
          />
        </div>
      </div>

      <div className={c.actions}>
        <IconButton
          className={c.sortArrow}
          variant="text"
          mode="card"
          icon={Icon.ArrowUp}
          color="var(--card-text-color)"
          size={24}
          aria-label="Move shortcut up"
          onClick={moveShortcutUp}
          disabled={isFirst}
        />
        <IconButton
          onClick={deleteInEditShortcut}
          variant="text"
          mode="card"
          icon={Icon.Trash}
          color="var(--card-text-color)"
          size={24}
          aria-label="Delete shortcut"
        />
        <IconButton
          className={c.sortArrow}
          variant="text"
          mode="card"
          icon={Icon.ArrowDown}
          color="var(--card-text-color)"
          size={24}
          aria-label="Move shortcut down"
          onClick={moveShortcutDown}
          disabled={isLast}
        />
      </div>
    </SortableListItem>
  );
};
